import {
  CheckOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DeleteTwoTone,
  EditTwoTone,
  PlusOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  BaselineItem,
  CREATE_BASELINE,
  CREATE_PROJECT,
  DELETE_BASELINE,
  ErrorResponse,
  LIST_BASELINES,
  LIST_METRICS,
  LIST_PROJECTS,
  MetricItem,
  MetricUnit,
  MetricUnitType,
  Project,
  UPDATE_BASELINE,
} from '@frontend/data-access';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { RichEditorSerialized } from '@frontend/shared/ui';
import { formatNumber } from '@frontend/shared/util';
import {
  App,
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Table,
  Tooltip,
} from 'antd';
import { ColumnType } from 'antd/lib/table/interface';
import React, { useMemo, useState } from 'react';
import { TabHeader } from '../components';
import useTranslation from 'next-translate/useTranslation';

interface SelectProjectProps {
  label: string;
  value: string;
  unit: string | undefined;
}

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: string;
  record: BaselineItem;
}

interface EditableColumn extends ColumnType<BaselineItem> {
  editable?: boolean;
}

export function BaselineData() {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [form] = Form.useForm();
  const [projectForm] = Form.useForm();
  const [baselineData, setBaselineData] = useState<BaselineItem[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<MetricUnit>();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data } = useQuery<{ listMetrics: MetricItem[] }>(LIST_METRICS);
  const { data: defaultBaselineData, loading } = useQuery<{
    listBaseline: BaselineItem[];
  }>(LIST_BASELINES, {
    onCompleted: (data) => {
      setBaselineData([...data.listBaseline]);
    },
  });

  const { data: projectsData } = useQuery<{ listProjects: Project[] }>(
    LIST_PROJECTS
  );

  const projectOptions = useMemo(() => {
    const options =
      projectsData?.listProjects?.map((project) => ({
        value: project.id,
        label: project.name,
      })) ?? [];
    return [{ value: null, label: t('no_project') }, ...options];
  }, [projectsData, t]);

  const handleError = (error: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: error.message,
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };

  const [createBaseline, { loading: createLoading }] = useMutation(
    CREATE_BASELINE,
    {
      refetchQueries: [{ query: LIST_BASELINES }],
      onError: handleError,
    }
  );

  const [updateBaseline, { loading: updateLoading }] = useMutation(
    UPDATE_BASELINE,
    {
      refetchQueries: [{ query: LIST_BASELINES }],
      onError: handleError,
    }
  );

  const [createProject, { loading: createProjectLoading }] = useMutation(
    CREATE_PROJECT,
    {
      refetchQueries: [{ query: LIST_PROJECTS }],
      onError: handleError,
    }
  );

  const [deleteBaseline, { loading: deleteLoading }] = useMutation(
    DELETE_BASELINE,
    {
      refetchQueries: [{ query: LIST_BASELINES }],
      onError: handleError,
    }
  );

  const handleAddBaseline = () => {
    const newKey = Date.now().toString();
    const newRow: BaselineItem = {
      id: newKey,
      metric: undefined,
      project: undefined,
      baseline: undefined,
    };

    setBaselineData([
      ...(defaultBaselineData?.listBaseline ?? baselineData),
      newRow,
    ]);
    setEditingKey(newKey);

    // Reset the form for the new row
    form.setFieldsValue({
      metric: undefined,
      project: null,
      baseline: undefined,
      unit: undefined,
    });
  };

  const handleDeleteBaseline = (key: string | undefined) => {
    if (!key) return;

    setDeleteId(key);

    deleteBaseline({
      variables: { id: key },
      onCompleted: () => {
        // Remove from local state
        setBaselineData(baselineData.filter((item) => item.id !== key));
        setDeleteId(null);
      },
    });
  };

  const cancelBaselineEdit = () => {
    // Remove the row if it's a new entry (hasn't been saved yet)
    const currentEditingRow = baselineData.find(
      (item) => item.id === editingKey
    );
    if (currentEditingRow && !currentEditingRow.metric) {
      setBaselineData(baselineData.filter((item) => item.id !== editingKey));
    }
    setEditingKey(null);
  };

  const handleAddProject = () => {
    projectForm.validateFields().then((values) => {
      if (values.projectName?.trim()) {
        createProject({
          variables: { name: values.projectName.trim() },
        });
        projectForm.resetFields();
      }
    });
  };

  const saveBaseline = async (key: string | undefined) => {
    if (!key) return;

    const row = await form.validateFields();
    const newData = [...baselineData];
    const existingBaseline = newData.find((item) => key === item.id);

    if (existingBaseline) {
      const isNewEntry = !existingBaseline.metric;

      if (isNewEntry) {
        // Create new baseline
        await createBaseline({
          variables: {
            input: {
              metricId: row.metric,
              projectId: row.project,
              baseline: parseInt(row.baseline, 10),
            },
          },
        });
        cancelBaselineEdit();
      } else {
        // Update existing baseline
        await updateBaseline({
          variables: {
            input: {
              id: key,
              projectId:
                existingBaseline.project?.id === row.project
                  ? undefined
                  : row.project || null,
              baseline: parseInt(row.baseline, 10),
            },
          },
        });
      }
    }
    setEditingKey(null);
  };

  const editBaseline = (record: BaselineItem) => {
    form.setFieldsValue({
      metric: record.metric?.id,
      project: record.project?.id ?? null,
      baseline: record.baseline,
    });
    setSelectedUnit(record.metric?.unit);
    setEditingKey(record.id ?? null);
  };

  const isBaselineEditing = (record: BaselineItem) => record.id === editingKey;

  const baselineColumns: EditableColumn[] = [
    {
      title: t('metric'),
      dataIndex: 'metric',
      key: 'metric',
      editable: true,
      render: (_, record: BaselineItem): React.ReactNode => {
        const editable = isBaselineEditing(record);
        const isNewEntry = !record.metric;
        return editable ? (
          <Form.Item
            name="metric"
            className="!m-0"
            style={{ width: '160px' }}
            rules={[
              { required: true, message: t('baseline_select_metric_required') },
            ]}
          >
            <Select<SelectProjectProps>
              disabled={!isNewEntry}
              popupClassName="!w-[300px]"
              options={data?.listMetrics.map((m) => {
                const isQualitative =
                  m.unit?.type === MetricUnitType.QUALITATIVE;
                return {
                  label: m.name + (isQualitative ? '' : ` (${m.unit?.label})`),
                  value: m.id,
                  unit: m.unit?.id,
                };
              })}
              onChange={(_, option) => {
                form.setFieldValue(
                  'unit',
                  (option as SelectProjectProps)?.unit
                );
                setSelectedUnit(
                  data?.listMetrics.find(
                    (m) => m.id === (option as SelectProjectProps)?.value
                  )?.unit
                );
              }}
            />
          </Form.Item>
        ) : (
          <span>{record.metric?.name}</span>
        );
      },
    },
    {
      title: (
        <div className="space-x-2">
          <span>{t('project')}</span>
          <Tooltip
            title={() => (
              <RichEditorSerialized value={t('baseline_project_tooltip')} />
            )}
          >
            <QuestionCircleOutlined className="cursor-pointer" />
          </Tooltip>
        </div>
      ),
      dataIndex: 'project',
      key: 'project',
      editable: true,
      render: (_, record: BaselineItem): React.ReactNode => {
        const editable = isBaselineEditing(record);
        return editable ? (
          <Form.Item name="project" className="!m-0" style={{ width: '160px' }}>
            <Select
              showSearch
              options={projectOptions}
              filterOption={(input, option) =>
                ((option?.label as string) ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              dropdownMatchSelectWidth={false}
              popupClassName="!w-[400px]"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <div className="!w-full flex gap-2 py-1 px-2 items-center">
                    <Form form={projectForm} className="!w-full flex gap-2">
                      <Form.Item
                        name="projectName"
                        className="!m-0 !w-full"
                        rules={[
                          {
                            required: true,
                            message: t('baseline_project_name_required'),
                          },
                        ]}
                      >
                        <Input
                          placeholder={t('baseline_project_name_placeholder')}
                        />
                      </Form.Item>
                      <Button
                        type="text"
                        onClick={handleAddProject}
                        loading={createProjectLoading}
                      >
                        {t('baseline_add_item')}
                      </Button>
                    </Form>
                  </div>
                </>
              )}
            />
          </Form.Item>
        ) : (
          <span>{record.project?.name || t('no_project')}</span>
        );
      },
    },
    {
      title: (
        <div className="space-x-2">
          <span>{t('baseline')}</span>
          <Tooltip
            title={() => (
              <RichEditorSerialized value={t('baseline_amount_tooltip')} />
            )}
          >
            <QuestionCircleOutlined className="cursor-pointer" />
          </Tooltip>
        </div>
      ),
      key: 'baseline',
      width: 160,
      editable: true,
      render: (record: BaselineItem) => {
        const editable = isBaselineEditing(record);
        const isQualitative =
          (record.metric?.unit?.type || selectedUnit?.type) ===
          MetricUnitType.QUALITATIVE;
        return editable ? (
          <Form.Item
            name="baseline"
            className="!m-0"
            style={{ width: '160px' }}
            rules={[
              { required: true, message: t('baseline_enter_required') },
              {
                validator: async (_, value) => {
                  if (value) {
                    const num = parseInt(value, 10);
                    if (isNaN(num)) {
                      throw new Error(t('baseline_invalid_number'));
                    }
                    if (num > 2147483647 || num < -2147483648) {
                      throw new Error(t('baseline_value_length_exceeded'));
                    }
                  }
                },
              },
            ]}
          >
            <InputNumber
              className={isQualitative ? '!rounded-md' : ''}
              addonAfter={!isQualitative && selectedUnit?.label}
            />
          </Form.Item>
        ) : (
          <span>
            {formatNumber(record.baseline)}{' '}
            {isQualitative ? '' : record.metric?.unit?.label}
          </span>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record: BaselineItem) => {
        const editable = isBaselineEditing(record);
        return editable ? (
          <span className="flex flex-row">
            <Button
              type="link"
              onClick={() => saveBaseline(record.id)}
              loading={createLoading || updateLoading}
              icon={<CheckOutlined />}
            />
            <Button
              type="link"
              onClick={cancelBaselineEdit}
              icon={<CloseOutlined />}
            />
          </span>
        ) : (
          <div className="flex fl">
            <Button
              type="text"
              disabled={editingKey !== null}
              onClick={() => editBaseline(record)}
              icon={<EditTwoTone twoToneColor="#0473EA" />}
            />
            <Popconfirm
              title={t('baseline_delete_confirm')}
              onConfirm={() => handleDeleteBaseline(record.id)}
              okText={t('yes')}
              cancelText={t('no')}
            >
              <Button
                type="text"
                icon={<DeleteTwoTone twoToneColor="#FF4D4F" />}
                loading={deleteLoading && deleteId === record.id}
              />
            </Popconfirm>
          </div>
        );
      },
    },
  ];
  const EditableCell: React.FC<EditableCellProps> = ({
    editing,
    dataIndex,
    title,
    record,
    children,
    ...restProps
  }) => {
    return <td {...restProps}>{children}</td>;
  };

  const mergedBaselineColumns = baselineColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: BaselineItem): EditableCellProps => ({
        record,
        dataIndex: col.dataIndex as string,
        title: col.title as string,
        editing: isBaselineEditing(record),
      }),
    };
  }) as ColumnType<BaselineItem>[];

  return (
    <div className="space-y-6">
      <TabHeader
        title={t('baseline_data_title')}
        description={t('baseline_data_description')}
      />
      <Form form={form} component={false}>
        <Table
          bordered
          size="small"
          rowKey="id"
          scroll={{ x: 'max-content' }}
          loading={loading}
          columns={mergedBaselineColumns}
          dataSource={baselineData}
          pagination={false}
          rowClassName="editable-row"
          components={{ body: { cell: EditableCell } }}
        />
      </Form>

      <Button
        type="default"
        shape="round"
        icon={<PlusOutlined />}
        onClick={handleAddBaseline}
        className="mt-4"
      >
        {t('baseline_add_button')}
      </Button>
    </div>
  );
}
